import { useState } from 'react';
import { Button, Card, Form, InputGroup, Spinner } from 'react-bootstrap';
import { visitApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { formatDate, formatDateTime, formatMoney } from '../../utils/format';

/**
 * What this patient has been charged over all their visits, what came in, and
 * what is still owed. Reception can settle an outstanding visit from here.
 */
export default function PaymentCard({ payment, timeline = [], onChanged }) {
  const toast = useToast();
  const { can } = useAuth();

  const [collecting, setCollecting] = useState(null); // visit being settled
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);

  const billed = payment?.billed || 0;
  const paid = payment?.paid || 0;
  const due = Math.max(0, billed - paid);
  const settled = billed > 0 && due === 0;

  const unpaid = timeline
    .filter((v) => v.status !== 'cancelled')
    .map((v) => ({ ...v, balance: Math.max(0, (v.fees?.total || 0) - (v.fees?.paid || 0)) }))
    .filter((v) => v.balance > 0)
    .sort((a, b) => new Date(a.visitDate) - new Date(b.visitDate));

  function startCollecting(visit) {
    setCollecting(visit);
    setAmount(String(visit.balance));
  }

  // Every instalment across every visit, newest first.
  const history = timeline
    .flatMap((v) => (v.payments || []).map((p) => ({ ...p, visitDate: v.visitDate })))
    .sort((a, b) => new Date(b.receivedAt) - new Date(a.receivedAt));

  async function collect() {
    const extra = Number(amount);
    if (!extra || extra <= 0) {
      toast.warn('Enter the amount received');
      return;
    }
    if (extra > collecting.balance) {
      toast.warn(`That is more than the ${formatMoney(collecting.balance)} outstanding on this visit`);
      return;
    }

    setSaving(true);
    try {
      await visitApi.addPayment(collecting._id, { amount: extra, mode: 'cash' });
      toast.success(`${formatMoney(extra)} received`);
      setCollecting(null);
      setAmount('');
      onChanged?.();
    } catch (err) {
      toast.error(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>
          <i className="bi bi-cash-coin me-2" />
          Payment
        </span>
        {billed > 0 && (
          <span className={`badge ${settled ? 'bg-success' : 'bg-danger'}`}>
            <i className={`bi ${settled ? 'bi-check-circle' : 'bi-exclamation-circle'} me-1`} />
            {settled ? 'Completed' : `Due ${formatMoney(due)}`}
          </span>
        )}
      </Card.Header>

      <Card.Body>
        {billed === 0 ? (
          <div className="text-muted small text-center py-2">Nothing has been charged to this patient yet.</div>
        ) : (
          <>
            <div className="pay-rows">
              <div className="pay-row">
                <span className="text-muted">Total charged</span>
                <span className="fw-semibold">{formatMoney(billed)}</span>
              </div>
              <div className="pay-row">
                <span className="text-muted">Received</span>
                <span className="fw-semibold text-success">{formatMoney(paid)}</span>
              </div>
              <div className="pay-row pay-row-total">
                <span className="fw-semibold">Still to pay</span>
                <span className={`fw-bold fs-5 ${due > 0 ? 'text-danger' : 'text-success'}`}>
                  {formatMoney(due)}
                </span>
              </div>
            </div>

            <div className="text-muted mt-2" style={{ fontSize: '0.76rem' }}>
              Across {payment.visits} visit{payment.visits === 1 ? '' : 's'}
            </div>

            {unpaid.length > 0 && (
              <>
                <hr />
                <div className="fw-semibold small mb-2">Visits with a balance</div>

                {unpaid.map((v) => (
                  <div key={v._id} className="pay-due-row">
                    <div className="flex-grow-1">
                      <div className="small fw-semibold">{formatDate(v.visitDate)}</div>
                      <div className="text-muted" style={{ fontSize: '0.74rem' }}>
                        Charged {formatMoney(v.fees?.total || 0)} &middot; paid {formatMoney(v.fees?.paid || 0)}
                      </div>
                    </div>

                    {collecting?._id === v._id ? (
                      <div className="d-flex gap-1 align-items-center">
                        <InputGroup size="sm" style={{ width: 130 }}>
                          <InputGroup.Text>&#8377;</InputGroup.Text>
                          <Form.Control
                            autoFocus
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') collect();
                              if (e.key === 'Escape') setCollecting(null);
                            }}
                          />
                        </InputGroup>
                        <Button size="sm" className="btn-clinic" disabled={saving} onClick={collect}>
                          {saving ? <Spinner animation="border" size="sm" /> : <i className="bi bi-check-lg" />}
                        </Button>
                        <Button size="sm" variant="light" onClick={() => setCollecting(null)} disabled={saving}>
                          <i className="bi bi-x-lg" />
                        </Button>
                      </div>
                    ) : (
                      <div className="d-flex gap-2 align-items-center">
                        <span className="text-danger fw-semibold small">{formatMoney(v.balance)}</span>
                        {can('reception', 'doctor') && (
                          <Button size="sm" variant="outline-primary" onClick={() => startCollecting(v)}>
                            Collect
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}

            {history.length > 0 && (
              <>
                <hr />
                <div className="fw-semibold small mb-2">
                  Payment history
                  <span className="text-muted fw-normal ms-2">
                    {history.length} entr{history.length === 1 ? 'y' : 'ies'}
                  </span>
                </div>

                <div className="pay-history">
                  {history.map((p, i) => (
                    <div className="pay-history-row" key={p._id || i}>
                      <div className="pay-history-amount">+{formatMoney(p.amount)}</div>
                      <div className="flex-grow-1 min-w-0">
                        <div className="small">{formatDateTime(p.receivedAt)}</div>
                        <div className="text-muted" style={{ fontSize: '0.74rem' }}>
                          {String(p.mode || 'cash').toUpperCase()}
                          {p.receivedBy?.name ? ` · ${p.receivedBy.name}` : ''}
                          {p.note ? ` · ${p.note}` : ''}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}
